import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Target, Eye, Award, MapPin, Users, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import teamImage from "@/assets/team-collaboration.jpg";
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();
  
  const values = [
    {
      icon: Target,
      title: t('about.mission_title'),
      description: t('about.mission_desc'),
      gradient: "from-gradient-pink/20 to-gradient-purple/20",
    },
    {
      icon: Eye,
      title: t('about.vision_title'),
      description: t('about.vision_desc'),
      gradient: "from-gradient-purple/20 to-gradient-blue/20",
    },
    {
      icon: Award,
      title: t('about.values_title'),
      description: t('about.values_desc'),
      gradient: "from-gradient-blue/20 to-gradient-cyan/20",
    },
  ];

  const milestones = [
    { year: "2025", event: t('about.milestone_2025'), icon: Award },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-shadow-glow">
              {t('about.title')}
            </h1>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`p-8 card-dark text-center hover:glow-effect transition-all duration-300 h-full bg-gradient-to-br ${value.gradient}`}>
                  <div className="w-14 h-14 rounded-lg gradient-brand flex items-center justify-center mx-auto mb-4 glow-effect">
                    <value.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                  <p className="text-foreground/70">
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          <Card className="p-12 card-dark gradient-border mb-20 overflow-hidden relative">
            <div className="absolute inset-0 opacity-10">
              <img src={teamImage} alt="Team collaboration" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('about.story_title')}</h2>
              <p className="text-lg text-foreground/80 leading-relaxed mb-4">
                {t('about.story_p1')}
              </p>
              <p className="text-lg text-foreground/80 leading-relaxed">
                {t('about.story_p2')}
              </p>
            </div>
          </Card>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-8 text-center">{t('about.journey_title')}</h2>
            <div className="max-w-3xl mx-auto">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="flex items-start gap-6 mb-8"
                >
                  <div className="w-16 h-16 rounded-full gradient-brand flex items-center justify-center flex-shrink-0 glow-effect">
                    <milestone.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-grow pt-3">
                    <div className="text-2xl font-bold gradient-brand-text mb-2">{milestone.year}</div>
                    <p className="text-lg text-foreground/80">{milestone.event}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mb-20">
            <h2 className="text-3xl font-bold mb-6">{t('about.location_title')}</h2>
            <Card className="max-w-md mx-auto p-8 card-dark gradient-border">
              <MapPin className="w-12 h-12 text-accent mx-auto mb-4" />
              <p className="text-lg font-semibold mb-2">
                <strong>{t('about.location_company')}</strong>
              </p>
              <p className="text-foreground/70 mb-1">{t('about.location_city')} 🇩🇪</p>
              <p className="text-foreground/70">{t('about.location_serving')}</p>
            </Card>
          </div>

          <Card className="p-12 card-dark gradient-border text-center glow-effect">
            <h2 className="text-3xl font-bold mb-4">{t('about.cta_title')}</h2>
            <p className="text-lg text-foreground/70 mb-8 max-w-2xl mx-auto">
              {t('about.cta_subtitle')}
            </p>
            <Link to="/contact">
              <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-6">
                {t('about.cta_button')}
              </Button>
            </Link>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
